"use client"

import { useState, useEffect } from "react"
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react"
import { format, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isPast } from "date-fns"
import { ref, set, onValue, get, update } from "../lib/firebase-database"
import { db } from "../firebase"

function BookingCalendar({ villaId, isOwner = false, onDateSelect, showCurrentMonth = false }) {
  const [selectedDates, setSelectedDates] = useState([])
  const [bookedDates, setBookedDates] = useState([])
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd })

  useEffect(() => {
    if (!villaId) return

    const bookingsRef = ref(db, `bookings/${villaId}`)
    const unsubscribe = onValue(bookingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const bookingsData = snapshot.val()
        const dates = []
        Object.values(bookingsData).forEach((booking) => {
          if (booking.startDate && booking.endDate) {
            const start = new Date(booking.startDate)
            const end = new Date(booking.endDate)
            const currentDate = new Date(start)
            while (currentDate <= end) {
              dates.push(new Date(currentDate))
              currentDate.setDate(currentDate.getDate() + 1)
            }
          }
        })
        setBookedDates(dates)
      } else {
        setBookedDates([])
      }
    })

    return () => unsubscribe()
  }, [villaId])

  const handleDateClick = (date) => {
    if (!isOwner) return // Only owners can select dates
    if (isPast(date) && !isToday(date)) return // Prevent past date selection

    const isSelected = selectedDates.some((d) => isSameDay(d, date))
    let newSelectedDates
    if (isSelected) {
      newSelectedDates = selectedDates.filter((d) => !isSameDay(d, date))
    } else {
      newSelectedDates = [...selectedDates, date]
    }
    setSelectedDates(newSelectedDates)

    if (onDateSelect) {
      onDateSelect(newSelectedDates)
    }
  }

  const markDatesAsBooked = async () => {
    if (!isOwner || !villaId || !selectedDates.length) return

    setIsLoading(true)
    setError(null)

    try {
      for (const date of selectedDates) {
        const bookingId = `${date.getTime()}`
        await set(ref(db, `bookings/${villaId}/${bookingId}`), {
          startDate: date.toISOString(),
          endDate: date.toISOString(),
          createdAt: new Date().toISOString(),
          status: "booked",
        })
      }
      setSelectedDates([])
    } catch (err) {
      setError("Failed to update booking. Please try again.")
      console.error("Error marking dates as booked:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const markDatesAsAvailable = async () => {
    if (!isOwner || !villaId || !selectedDates.length) return

    setIsLoading(true)
    setError(null)

    try {
      const bookingsRef = ref(db, `bookings/${villaId}`)
      const snapshot = await get(bookingsRef)
      const currentBookings = snapshot.val() || {}

      const updates = {}
      Object.entries(currentBookings).forEach(([key, booking]) => {
        const start = new Date(booking.startDate)
        selectedDates.forEach((selectedDate) => {
          if (isSameDay(start, selectedDate)) {
            updates[key] = null
          }
        })
      })

      if (Object.keys(updates).length > 0) {
        await update(ref(db, `bookings/${villaId}`), updates)
      }
      setSelectedDates([])
    } catch (err) {
      setError("Failed to update availability. Please try again.")
      console.error("Error marking dates as available:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const isDateBooked = (date) => {
    return bookedDates.some((d) => isSameDay(d, date))
  }

  const isDateSelected = (date) => {
    return selectedDates.some((d) => isSameDay(d, date))
  }

  const previousMonth = () => {
    if (showCurrentMonth) return
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))
  }

  const nextMonth = () => {
    if (showCurrentMonth) return
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
  }

  return (
    <div className="w-full max-w-md mx-auto p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-md p-3 md:p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-3 md:mb-4 gap-2">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
            <h2 className="text-base md:text-lg font-semibold text-gray-800">{format(currentMonth, "MMMM yyyy")}</h2>
          </div>
          {!showCurrentMonth && (
            <div className="flex space-x-2">
              <button
                onClick={previousMonth}
                className="p-1 md:px-3 md:py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs transition-colors"
              >
                <ChevronLeft className="h-4 w-4 md:hidden" />
                <span className="hidden md:inline">Previous</span>
              </button>
              <button
                onClick={nextMonth}
                className="p-1 md:px-3 md:py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs transition-colors"
              >
                <ChevronRight className="h-4 w-4 md:hidden" />
                <span className="hidden md:inline">Next</span>
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-7 gap-0.5 mb-1 md:mb-2">
          {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
            <div key={index} className="text-center text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: monthStart.getDay() }).map((_, index) => (
            <div key={`empty-${index}`} className="h-8 md:h-10 lg:h-12" />
          ))}

          {monthDays.map((date, index) => {
            const isSelected = isDateSelected(date)
            const isBooked = isDateBooked(date)
            const isPastDate = isPast(date) && !isToday(date)

            return (
              <button
                key={index}
                onClick={() => handleDateClick(date)}
                disabled={!isOwner || isPastDate}
                className={`
        h-8 md:h-10 lg:h-12 rounded-md flex items-center justify-center text-xs
        transition-colors relative
        ${isSelected ? "bg-purple-600 text-white" : "bg-white"}
        ${isBooked ? "bg-orange-200 text-orange-800" : ""}
        ${!isSameMonth(date, currentMonth) ? "text-gray-300" : ""}
        ${isPastDate ? "opacity-50 cursor-not-allowed" : "text-gray-800"}
        ${!isOwner ? "cursor-default" : "cursor-pointer"}
      `}
              >
                {format(date, "d")}
              </button>
            )
          })}
        </div>

        {isOwner && (
          <div className="mt-3 md:mt-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={markDatesAsBooked}
              disabled={!selectedDates.length || isLoading}
              className={`
                w-full py-2 px-3 rounded-md font-medium text-xs md:text-sm
                ${selectedDates.length && !isLoading
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }
                transition-colors
              `}
            >
              {isLoading ? "Updating..." : "Mark as Unavailable"}
            </button>
            <button
              onClick={markDatesAsAvailable}
              disabled={!selectedDates.length || isLoading}
              className={`
                w-full py-2 px-3 rounded-md font-medium text-xs md:text-sm
                ${selectedDates.length && !isLoading
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }
                transition-colors
              `}
            >
              {isLoading ? "Updating..." : "Mark as Available"}
            </button>
          </div>
        )}

        {error && <div className="mt-2 md:mt-3 p-2 bg-red-100 text-red-700 rounded-md text-xs">{error}</div>}

        <div className="mt-2 md:mt-3">
          <div className="flex flex-wrap items-center gap-2 text-[10px] md:text-xs text-gray-600">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 md:w-3 md:h-3 bg-orange-200 rounded"></div>
              <span>Unavailable</span>
            </div>
            {isOwner && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-purple-600 rounded"></div>
                <span>Selected</span>
              </div>
            )}
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 md:w-3 md:h-3 bg-gray-200 opacity-50 rounded"></div>
              <span>Past</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingCalendar

